import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-bootstrap';
import { sprayService, SprayOperator, SprayOperatorInput } from '../../services/sprayService';
import { listUsers } from '../../services/userService';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { useFormDirty } from '../../hooks/useFormDirty';
import { useBeforeUnloadWarning } from '../../hooks/useBeforeUnloadWarning';
import { useConfirmDiscard } from '../../hooks/useConfirmDiscard';
import PageHeader from '../shell/PageHeader';
import { Field, FormSection, FormShell, SaveBar, SaveBarStatus } from '../patterns';

interface Props {
  operator?: SprayOperator;
  onSave: (operator: SprayOperator) => void;
  onCancel: () => void;
}

interface CandidateUser {
  _id: string;
  name?: string;
  username?: string;
  role?: { isFarmAdministrator?: boolean; menus?: string[] };
}

function isEligibleForLinkage(_user: CandidateUser): boolean {
  // Phase 3.8 — under the new linkage-based operator gate, any user can be
  // linked as a SprayOperator. The form no longer pre-filters; tenant admins
  // make the call. Keep the function (rather than deleting it) so future
  // restrictions have an obvious extension point.
  return true;
}

function buildCandidateUsers(
  users: CandidateUser[],
  operators: SprayOperator[],
  currentOperatorId?: string
): CandidateUser[] {
  const takenUserIds = new Set<string>();
  for (const op of operators) {
    if (!op.userId) continue;
    if (op._id === currentOperatorId) continue;
    takenUserIds.add(String(op.userId));
  }
  return users.filter(isEligibleForLinkage).filter(u => !takenUserIds.has(u._id));
}

const SprayOperatorForm: React.FC<Props> = ({ operator, onSave, onCancel }) => {
  const { t } = useTranslation();
  const isEdit = Boolean(operator);

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('spray.title'), to: '/protection' },
    { label: t('spray.operators.title'), to: '/protection/operators' },
    { label: isEdit
        ? t('spray.operators.editTitle', { defaultValue: 'Edit operator' })
        : t('spray.pageHeader.actions.newOperator') },
  ]);

  const [name, setName] = useState(operator?.name ?? '');
  const [licenseNo, setLicenseNo] = useState(operator?.licenseNo ?? '');
  const [licenseExpiry, setLicenseExpiry] = useState(operator?.licenseExpiry?.substring(0, 10) ?? '');
  const [userId, setUserId] = useState<string>(operator?.userId ? String(operator.userId) : '');
  const [users, setUsers] = useState<CandidateUser[]>([]);
  const [allOperators, setAllOperators] = useState<SprayOperator[]>([]);
  const [nameError, setNameError] = useState('');
  const [licenseError, setLicenseError] = useState('');
  const [apiError, setApiError] = useState('');
  const [saving, setSaving] = useState(false);

  const { dirty } = useFormDirty(
    {
      name: operator?.name ?? '',
      licenseNo: operator?.licenseNo ?? '',
      licenseExpiry: operator?.licenseExpiry?.substring(0, 10) ?? '',
      userId: operator?.userId ? String(operator.userId) : '',
    },
    { name, licenseNo, licenseExpiry, userId },
  );
  useBeforeUnloadWarning(dirty);
  const confirmDiscard = useConfirmDiscard();

  useEffect(() => {
    listUsers().then((list: any[]) => setUsers(list || [])).catch(() => setUsers([]));
    sprayService.getOperators().then(setAllOperators).catch(() => setAllOperators([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const candidateUsers = buildCandidateUsers(users, allOperators, operator?._id);

  const validate = (): boolean => {
    let valid = true;
    setNameError('');
    setLicenseError('');
    setApiError('');
    if (!name.trim()) { setNameError(t('spray.operators.nameRequired')); valid = false; }
    if (!licenseNo.trim()) { setLicenseError(t('spray.operators.licenseRequired')); valid = false; }
    return valid;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    const data: SprayOperatorInput = {
      name: name.trim(),
      licenseNo: licenseNo.trim(),
      licenseExpiry: licenseExpiry || undefined,
      userId: userId === '' ? (operator ? null : undefined) : userId,
    };
    try {
      const result = operator
        ? await sprayService.updateOperator(operator._id, data)
        : await sprayService.createOperator(data);
      onSave(result);
    } catch (err: any) {
      setApiError(err?.message ?? t('spray.operators.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void handleSave();
  };

  return (
    <div data-testid="spray-operator-form">
      <PageHeader
        title={isEdit
          ? t('spray.operators.editTitle', { defaultValue: 'Edit operator' })
          : t('spray.pageHeader.actions.newOperator')}
        meta={t('spray.operators.formMeta', { defaultValue: 'Operator identity, licence, and linked office user.' })}
      />

      {apiError && <Alert variant="danger" role="alert" className="mb-3">{apiError}</Alert>}

      <FormShell
        sections={[]}
        onSubmit={handleSubmit}
        saveBar={
          <SaveBar
            status={
              saving
                ? ({ kind: 'saving' } as SaveBarStatus)
                : apiError
                  ? ({ kind: 'error', message: apiError } as SaveBarStatus)
                  : dirty
                    ? ({ kind: 'dirty' } as SaveBarStatus)
                    : null
            }
            primaryAction={{
              label: saving
                ? t('patterns.saveBar.primary.saving')
                : apiError
                  ? t('patterns.saveBar.primary.retry')
                  : t('patterns.saveBar.primary.save'),
              onClick: handleSave,
              disabled: (!dirty && !apiError) || saving,
            }}
            secondaryActions={[
              { label: t('common.cancel'), tone: 'subtle', onClick: () => { if (confirmDiscard(dirty)) onCancel(); }, disabled: saving },
            ]}
          />
        }
      >
        <FormSection
          id="operator-details"
          title={t('spray.operators.sections.details', { defaultValue: 'Operator details' })}
        >
          <div className="pat-field-grid pat-field-grid--2">
            <Field
              label={t('spray.operators.name')}
              required
              controlId="operator-name"
              errorText={nameError || undefined}
            >
              <input
                id="operator-name"
                type="text"
                className="pat-input"
                value={name}
                onChange={e => setName(e.target.value)}
                aria-invalid={Boolean(nameError) || undefined}
                aria-required="true"
              />
            </Field>
            <Field
              label={t('spray.operators.licenseNo')}
              required
              controlId="operator-licenseNo"
              errorText={licenseError || undefined}
            >
              <input
                id="operator-licenseNo"
                type="text"
                className="pat-input"
                value={licenseNo}
                onChange={e => setLicenseNo(e.target.value)}
                aria-invalid={Boolean(licenseError) || undefined}
                aria-required="true"
              />
            </Field>
            <Field
              label={t('spray.operators.licenseExpiry')}
              controlId="operator-licenseExpiry"
            >
              <input
                id="operator-licenseExpiry"
                type="date"
                className="pat-input"
                value={licenseExpiry}
                onChange={e => setLicenseExpiry(e.target.value)}
              />
            </Field>
            <Field
              label={t('spray.operators.linkedUser')}
              controlId="operator-linkedUser"
              helpText={t('spray.operators.linkedUserHelp')}
            >
              <select
                id="operator-linkedUser"
                className="pat-input"
                value={userId}
                onChange={e => setUserId(e.target.value)}
              >
                <option value="">{t('spray.operators.linkedUserNone')}</option>
                {candidateUsers.map(u => (
                  <option key={u._id} value={u._id}>{u.name || u.username || u._id}</option>
                ))}
              </select>
            </Field>
          </div>
        </FormSection>
      </FormShell>
    </div>
  );
};

export default SprayOperatorForm;
